import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'

// Content is static for now. Migrate to the same Firestore-backed pattern as
// TermsOfServicePage (with an admin edit page) post-launch if frequent
// updates become necessary.
const LAST_UPDATED = 'August 27, 2026'

const CONTENT = `
**One Day Dance LLC** ("One Day Dance", "we", "us") operates
[onedaydance.com](https://www.onedaydance.com), its shop, application and
ticketing services, and related client and participant portals. This notice
explains what we collect, why we use it, who receives it, and the choices
available to you.

We are a New York-based dance media company. We try to collect only what we
need to provide the site, respond to you, fulfill purchases, protect the
service, and improve our work.

## Information we collect

### Information you give us
- **Account information** — email address, display name, and authentication
  credentials managed by Firebase Authentication. We never receive your
  password in plain text.
- **Contact, application, and project information** — name, email, phone,
  organization, form answers, event details, uploaded files, and other
  information you choose to provide.
- **Purchase and fulfillment information** — purchased items, billing or
  shipping contact details, ticket information, and transaction status.
- **Testimonials and survey responses** — ratings, reviews, and follow-up text.
- **Newsletter information** — name, email, language preference, and mailing
  interests when you opt in.

### Information collected automatically
- **Service logs** — IP address, browser and device information, request time,
  requested page or function, and error or security information generated by
  hosting and server infrastructure.
- **Security signals** — Google reCAPTCHA Enterprise and Firebase App Check
  process device, network, and interaction signals to distinguish legitimate
  visitors from automated abuse. Rate limits may temporarily associate an IP
  address or email address with a request count.
- **Optional analytics and performance information** — only after you permit
  analytics, Google Analytics for Firebase and Firebase Performance collect
  page, session, device, approximate-location, interaction, error, and loading
  information. We disable Google advertising storage and advertising
  personalization in our implementation.

### Payment information
Card details are collected directly by **Stripe**. We do not receive or store
your complete card number, CVC, or expiration date. Stripe sends us transaction
status, an opaque payment identifier, and limited payment-method details needed
for receipts, accounting, fraud prevention, and support.

## Cookies and similar technologies

This section covers cookies, local storage, IndexedDB, pixels, and similar
browser technology. On your first visit, optional categories are off until you
choose otherwise. Your selection lasts up to six months, after which we ask
again. You can reopen **Cookie Settings** in the footer at any time.

| Technology | Provider | Purpose | Category | Typical duration |
|---|---|---|---|---|
| \`odd-cookie-consent\` | One Day Dance | Remembers your privacy choices | Necessary | Up to 6 months |
| \`odd-shop-cart\` | One Day Dance | Keeps items in your cart | Necessary | Until cleared in your browser |
| Authentication storage | Google Firebase | Keeps you signed in and protects your account | Necessary | Until sign-out or browser cleanup |
| reCAPTCHA/App Check storage | Google | Bot detection and service security | Necessary | Varies; some identifiers may last up to 6 months |
| \`__stripe_mid\`, \`__stripe_sid\`, and related storage | Stripe | Payment security and fraud prevention; loaded when you enter a payment flow | Necessary for checkout | About 1 year and 30 minutes respectively |
| \`_ga\`, \`_ga_*\` | Google Analytics | Distinguishes visitors and sessions for site analytics | Optional analytics | Up to 2 years |
| Firebase Performance storage | Google Firebase | Measures errors, network requests, and loading performance | Optional analytics | Varies by configuration |
| Privacy-enhanced YouTube player storage | YouTube/Google | Plays an embedded video after you permit external media | Optional media | Set by YouTube; varies by feature and account state |

Necessary technologies do not require optional consent because they provide a
feature you requested, remember your privacy choice, secure the service, or
support a payment flow you entered. Rejecting optional technology does not
prevent ordinary use of the site. YouTube players remain blocked until external
media is allowed. If you withdraw analytics permission, the site refreshes to
stop telemetry and remove One Day Dance Google Analytics identifiers accessible
to us in that browser.

## How and why we use information

We use information under the following legal bases where those concepts apply:

- **Contract and steps at your request** — to answer service inquiries, process
  applications, sell and deliver tickets or products, provide portals, and
  perform our agreements with you.
- **Legitimate interests** — to operate, secure, troubleshoot, and improve the
  service; prevent fraud; keep appropriate business records; and understand
  aggregate service performance, where those interests are not overridden by
  your rights.
- **Consent** — for optional browser analytics, external media, and marketing
  email. You may withdraw consent without affecting earlier lawful processing.
- **Legal obligations** — for tax, accounting, fraud, safety, law-enforcement,
  dispute, and regulatory requirements.

We do not sell personal information or use it for cross-context behavioral
advertising. We do not provide personal information to advertisers.

## Service providers and recipients

We disclose information only as needed to operate the service, fulfill your
request, comply with law, or protect rights and safety:

| Provider | Service | Information involved |
|---|---|---|
| **Google Firebase and Google Cloud** | Hosting, authentication, database, file storage, logs, analytics, performance, and bot protection | Site records, account details, files, device and usage information appropriate to each service |
| **Stripe** | Payments and fraud prevention | Contact, order, transaction, and device information; card details go directly to Stripe |
| **Printify** | Print-on-demand shop fulfillment | Shipping contact details and ordered items |
| **Resend** | Transactional and opted-in email delivery | Recipient details and message contents |
| **YouTube** | Privacy-enhanced embedded playback | Device, network, and video interaction information after external media is allowed |

We may also disclose information to professional advisers, government bodies,
or counterparties when required by law, needed to protect rights or safety, or
part of a business reorganization subject to appropriate safeguards.

## International transfers

We are based in the United States, and our providers may process information in
the United States and other countries. Those countries may have different data
protection laws. Where required, we rely on provider contractual commitments,
approved standard contractual clauses, recognized transfer frameworks, or
another lawful transfer mechanism. Contact us for more information about a
specific transfer safeguard.

## Retention

We keep information only as long as reasonably necessary for the purpose
described above, including providing the service, maintaining security,
resolving disputes, and meeting tax, accounting, insurance, and legal duties.
Transaction and contract records are generally retained for the applicable
legal and financial recordkeeping period. Pending security and rate-limit
records are short-lived. Newsletter information remains active until you
unsubscribe, after which we retain a suppression record. Published testimonials
remain until removed or you ask us to remove them. Provider-controlled logs and
fraud records follow the provider's configured retention schedule.

## Your rights and choices

Depending on where you live, you may have the right to request access,
correction, deletion, restriction, objection, or a portable copy; withdraw
consent; and appeal or complain to your local data protection authority. You may
also opt out of marketing using any newsletter unsubscribe link and change
optional browser choices through **Cookie Settings** in the footer.

To exercise a privacy right, email **privacy@onedaydance.com**. We may need to
verify your identity and may retain records that law requires us to keep. We
will not discriminate against you for making a privacy request. Authorized
agents may submit requests where applicable, subject to verification.

## Security

We use HTTPS, access controls, Firebase security rules, App Check, encryption
provided by our infrastructure, and operational safeguards appropriate to the
service. No internet service can guarantee absolute security. If a breach
requires notice, we will notify affected people and authorities as required by
applicable law.

## Children

The public site is intended for a general audience and is not directed to
children under 13. We do not knowingly collect personal information from a
child under 13 without legally sufficient authorization. A parent or guardian
who believes a child submitted information should contact us. Additional youth
privacy protections may apply when a program knowingly serves a minor.

## Changes to this notice

If this notice changes materially, we will update the date above and provide
additional notice when appropriate. A privacy notice describes our practices;
it is not a request to waive privacy rights. We will request fresh consent when
the law requires it.

## Contact

Questions, requests, or complaints: **privacy@onedaydance.com**.

**One Day Dance LLC**, New York, United States. A physical mailing address is
available on request for formal correspondence.
`

export function PrivacyPolicyPage() {
  return (
    <div className="mx-auto max-w-3xl px-4 py-16">
      <h1 className="text-4xl font-bold tracking-tight mb-2">Privacy Policy</h1>
      <p className="text-sm text-muted-foreground mb-10">Last updated {LAST_UPDATED}</p>
      <div className="prose prose-sm max-w-none text-muted-foreground [&_a]:text-foreground [&_a]:underline [&_strong]:text-foreground [&_h2]:text-foreground [&_h2]:text-xl [&_h2]:font-semibold [&_h2]:mt-10 [&_h2]:mb-3 [&_h3]:text-foreground [&_h3]:text-base [&_h3]:font-semibold [&_h3]:mt-6 [&_h3]:mb-2 [&_table]:w-full [&_th]:text-foreground [&_th]:text-left [&_th]:pr-4 [&_th]:pb-2 [&_td]:align-top [&_td]:pr-4 [&_td]:py-1">
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{CONTENT}</ReactMarkdown>
      </div>
    </div>
  )
}
